import AvatarCompany from "@/components/company/avatar-company";
import { Suggestion } from "@/components/ai-elements/suggestion";
import {
  ScrollableEdges,
  ScrollContainer,
} from "@/components/ui/scroll-container";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { convertDateToDistanceNow } from "@/lib/date";
import { chatRecentCompaniesKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import { CompanyDTO } from "@shaarp/common";
import { keepPreviousData } from "@tanstack/react-query";
import { ChevronDownIcon, HistoryIcon } from "lucide-react";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

/**
 * One page is what the 11rem-tall stack shows at once (a `h-7` chip plus its
 * `gap-2` is 36px, so five fit): the first request pays for the chips the user
 * can actually see, and scrolling to the bottom asks for the next page.
 */
const RECENT_COMPANIES_PAGE_SIZE = 5;

/**
 * The ceiling the paging stops at. Each page is a full re-read of the history
 * on the API side (`limit` grows, there is no cursor), and this block is a
 * shortcut to a company just visited, not the history page — past ~30 chips
 * the search field is the faster way there.
 */
const RECENT_COMPANIES_MAX = 30;

/**
 * How long a page already read stays good. Two new conversations opened in the
 * same minute share one request, and a company opened in between joins the
 * list at the next read — a minute at worst, rather than at the next reload.
 */
const RECENT_COMPANIES_STALE_TIME_MS = 60_000;

/**
 * How far the "more below" button scrolls the stack: three chips and their
 * gaps, out of the five on screen. A full page would replace everything the
 * reader was looking at; overlapping by two keeps a landmark in view.
 */
const RECENT_COMPANIES_SCROLL_STEP_PX = 3 * 36;

/**
 * Gap between two chips of the entrance cascade, the stagger of the pinned
 * tactics above them (`getSuggestionCardAnimationStyle`), so both blocks rise
 * at the same pace.
 */
const RECENT_COMPANIES_CASCADE_STEP_MS = 60;

/**
 * Delay of a chip in the cascade, the title being step 0. Counted within its
 * own page: a later page lands under the fade and rises from its first chip,
 * instead of waiting out the delays of the chips already on screen.
 */
const cascadeDelay = (index: number): CSSProperties => {
  const step = (index % RECENT_COMPANIES_PAGE_SIZE) + 1;
  return {
    animationDelay: `calc(var(--cascade-start, 0ms) + ${step} * var(--cascade-step, ${RECENT_COMPANIES_CASCADE_STEP_MS}ms))`,
  };
};

/**
 * Chip widths for the placeholders, cycled so a page of skeletons reads as a
 * stack of company names of different lengths rather than a bar chart.
 */
const SKELETON_CHIP_WIDTHS = [
  "w-48",
  "w-36",
  "w-52",
  "w-40",
  "w-44",
] as const;

/**
 * How long ago the user last opened this company, in words ("il y a 3 jours").
 *
 * The sub-label this block was shipped with was the company's city or sector.
 * It answered "which company is this", which the name and logo already do
 * here, and said nothing about the one thing the section is sorted by. A date
 * is what "consultées récemment" is about, and it is the only thing that tells
 * two chips of the stack apart in the reader's own timeline.
 *
 * `convertDateToDistanceNow` is the app-wide relative date, in the user's
 * language and with its suffix — the same wording the prospecting history
 * shows for these very visits. `lastVisited` crosses the wire as a string
 * despite the `Date` on {@link CompanyDTO}, hence the `new Date`. It is
 * optional on the DTO and set by this endpoint on every row it returns: a chip
 * without one keeps no sub-label rather than an invented date.
 */
const lastVisitedLabel = (
  company: CompanyDTO,
  language: "en" | "fr",
): string | null =>
  company.lastVisited
    ? convertDateToDistanceNow(new Date(company.lastVisited), {
        languageOverride: language,
      })
    : null;

/**
 * A company as a chip: its logo flush left in a tight pill, the pill only as
 * wide as its own label, so the column reads as a stack of chips rather than a
 * list of rows. Only an unusually long name truncates; the date of the last
 * visit always stays readable.
 *
 * Under the pointer it lifts and its logo grows, the same hover vocabulary as
 * the tactic cards right above it (`chat-suggestion-card`). `transition-all`
 * comes from `buttonVariants`, and a transform moves nothing in the column.
 *
 * It rises into place with the cascade the mode pills and the tactics use
 * (`suggestion-cascade`). On a wrapper, not on the chip: the animation holds
 * its last `transform` once done, which would pin the chip and cancel the
 * hover lift.
 */
function RecentCompanyChip({
  company,
  index,
  isTouchSized,
  onSelect,
}: {
  company: CompanyDTO;
  index: number;
  /** The phone's row: a chip a thumb can hit, 44px tall. */
  isTouchSized: boolean;
  onSelect: (companyId: string) => void;
}) {
  const { currentLanguage } = useTranslation();
  const subLabel = lastVisitedLabel(company, currentLanguage);

  return (
    <div className="suggestion-cascade shrink-0" style={cascadeDelay(index)}>
      <Suggestion
        suggestion={company.id}
        variant="secondary"
        className={cn(
          "group gap-1.5 duration-200 hover:-translate-y-0.5 hover:shadow-sm",
          isTouchSized ? "h-11 pl-1.5 pr-4 text-sm" : "h-7 pl-1 pr-3",
        )}
        onClick={onSelect}
      >
        <AvatarCompany
          className={cn(
            "transition-transform duration-200 group-hover:scale-110",
            isTouchSized ? "size-8" : "size-5",
          )}
          company={company}
        />
        <span className="max-w-[160px] truncate font-medium">{company.name}</span>
        {subLabel && (
          <span className="whitespace-nowrap text-muted-foreground">
            · {subLabel}
          </span>
        )}
      </Suggestion>
    </div>
  );
}

/** Placeholder chips, shaped like the real ones they stand in for. */
function SkeletonChips({
  count,
  offset = 0,
  isTouchSized = false,
}: {
  count: number;
  offset?: number;
  isTouchSized?: boolean;
}) {
  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          key={`skeleton-${offset + index}`}
          aria-hidden
          className={cn(
            "shrink-0 rounded-full",
            isTouchSized ? "h-11" : "h-7",
            SKELETON_CHIP_WIDTHS[
              (offset + index) % SKELETON_CHIP_WIDTHS.length
            ],
          )}
        />
      ))}
    </>
  );
}

/**
 * "Recently viewed companies" row on the standalone new-conversation home
 * screen, below the tactics block. Reads the same per-user, team-scoped
 * history already serving the prospecting dashboard's recent-companies card
 * (`GET /my/user/history/companies`): a company page opened
 * (`FETCH_COMPANY`), or the preview panel of a company the chat cited, once it
 * resolves to a Shaarp company (`PREVIEW_COMPANY`).
 *
 * Loaded a page at a time: the endpoint enriches every company it returns
 * (assignations, listings, notes, integrations), so asking for a long list
 * up front would make the home screen wait on companies nobody scrolls to.
 * Skeleton chips stand in for the page in flight, first page included.
 *
 * A failed request is indistinguishable from an empty history here — the
 * component reads no error state — and both render nothing rather than an
 * error or an indefinite spinner: the welcome screen must not go down with
 * this block (see the `9de48af` fix for the suggestions pool incident).
 */
export function ChatRecentCompanies({
  layout = "column",
}: {
  /**
   * "column" is the desktop's capped stack under the centered composer.
   * "row" is the phone's welcome screen: one line of chips the thumb swipes,
   * which leaves the height to the greeting and the keyboard.
   */
  readonly layout?: "column" | "row";
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useDeviceFormat();

  const [limit, setLimit] = useState(RECENT_COMPANIES_PAGE_SIZE);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Stable, so the container is not handed a new callback on every render.
  const handleScrollableEdgesChange = useCallback(
    (edges: ScrollableEdges) => setCanScrollDown(edges.canScrollDown),
    [],
  );

  const scrollDownOnePage = () =>
    scrollRef.current?.scrollBy({
      top: RECENT_COMPANIES_SCROLL_STEP_PX,
      behavior: "smooth",
    });

  // Not team-scoped in the key, and a remount is not what makes that safe — a
  // cache surviving a remount is exactly what a cache is for. What makes it
  // safe is that switching team is a `window.location.reload()`
  // (`nav-team-switcher.tsx:83`), which drops the whole react-query cache with
  // the page: no entry read under the previous team can be served under the
  // next one. The day that switch becomes a soft one, this key needs the team
  // id — the endpoint's own team scoping (docs/decisions/0001) does not save
  // a client-side cache.
  //
  // `limit` is part of the key, so each page is its own cache entry;
  // `keepPreviousData` keeps the chips already on screen in place while the
  // next, longer page loads instead of emptying the stack under the cursor.
  //
  // `staleTime` spares the whole chain (a history read, a company read and
  // four enrichment queries) on every new conversation opened in a row, which
  // is how this screen is used. Not `Infinity`, unlike the generated composer
  // examples it otherwise follows: those are drawn once and never change,
  // while opening a company from these very chips changes this list — a
  // minute is short enough for that visit to show up on the way back. A
  // preview opened in the chat does not wait for it: recording one
  // invalidates these pages (`useRecordCompanyPreviewView`).
  //
  // `isLoading` is the first page and only the first page: once a page has
  // landed, a longer one in flight shows as `isPlaceholderData` instead — the
  // precise signal, where `isFetching` would also fire on a background refetch
  // of the page already displayed and append skeletons for nothing.
  const {
    data: companies,
    isLoading: isLoadingFirstPage,
    isFetching,
    isPlaceholderData: isLoadingMore,
  } = useApiQuery<CompanyDTO[]>({
    key: [...chatRecentCompaniesKeys.page(limit)],
    path: "/my/user/history/companies",
    params: { limit },
    placeholderData: keepPreviousData,
    staleTime: RECENT_COMPANIES_STALE_TIME_MS,
  });

  // A page shorter than asked for is the end of the history: the endpoint
  // returns up to `limit` rows, never a count. False while a longer page is in
  // flight (the page on screen is shorter than the `limit` now requested),
  // which is also when nothing should be asked for anyway.
  const hasMore =
    !!companies && companies.length >= limit && limit < RECENT_COMPANIES_MAX;

  // Reaching the bottom of the stack asks for the next page. Re-armed once the
  // page in flight has landed — the sentinel is often still in view then (a
  // page barely fills the stack), and that is exactly when the next one is due.
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore || isFetching) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setLimit((current) =>
          Math.min(current + RECENT_COMPANIES_PAGE_SIZE, RECENT_COMPANIES_MAX),
        );
      },
      { root, rootMargin: "48px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching]);

  // On a phone a company opens in the panel rising from the bottom, as from a
  // conversation, and the welcome screen stays under it.
  const openCompany = (companyId: string) => {
    const company = companies?.find(({ id }) => id === companyId);
    if (isMobile && company) {
      companyPreviewPanelStore.open(company, [company]);
      return;
    }
    navigate(`/company/${companyId}`);
  };

  if (!isLoadingFirstPage && !companies?.length) return null;

  if (layout === "row") {
    return (
      <div className="flex flex-col gap-1.5 w-full">
        <span className="flex gap-1.5 items-center px-3 text-sm text-muted-foreground">
          <HistoryIcon className="size-4" aria-hidden />
          {t("chat.new.recentCompanies.title")}
        </span>
        {/* The same pages as the column, the sentinel at the end of the row:
            the observer's root is this scroller, whichever axis it scrolls. */}
        <div
          ref={scrollRef}
          className="flex gap-2 items-center px-3 py-0.5 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {companies?.map((company, index) => (
            <RecentCompanyChip
              key={company.id}
              company={company}
              index={index}
              isTouchSized
              onSelect={openCompany}
            />
          ))}
          {isLoadingFirstPage && (
            <SkeletonChips count={RECENT_COMPANIES_PAGE_SIZE} isTouchSized />
          )}
          {isLoadingMore && (
            <SkeletonChips
              count={2}
              offset={companies?.length ?? 0}
              isTouchSized
            />
          )}
          {hasMore && (
            <div ref={sentinelRef} aria-hidden className="h-px w-px shrink-0" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2 w-full text-center">
      <span className="flex gap-1.5 items-center text-sm text-muted-foreground suggestion-cascade">
        <HistoryIcon className="size-4" aria-hidden />
        {t("chat.new.recentCompanies.title")}
      </span>
      {/* A centered column rather than the `Suggestions` scroll area: the whole
          history is taller than the room left under the tactics, so the stack
          is capped and scrolls. The cap is deliberately short — this block
          shares the half of the screen under the centered composer with the
          tactics, and a taller stack ran into the bottom of the window on a
          laptop. `ScrollContainer` hides the
          scrollbar, as the rest of the app does, and fades the edge it can
          still scroll towards so the hidden chips are visible as hidden.
          `shrink-0` from `buttonVariants` keeps every chip at its own
          height. */}
      <div className="relative w-full">
        <ScrollContainer
          ref={scrollRef}
          className="flex flex-col items-center gap-2 max-h-44"
          onScrollableEdgesChange={handleScrollableEdgesChange}
        >
          {companies?.map((company, index) => (
            <RecentCompanyChip
              key={company.id}
              company={company}
              index={index}
              isTouchSized={false}
              onSelect={openCompany}
            />
          ))}
          {isLoadingFirstPage && (
            <SkeletonChips count={RECENT_COMPANIES_PAGE_SIZE} />
          )}
          {/* Two, not a full page: they only have to prove the stack continues
              below the fade while the next page is on the wire. */}
          {isLoadingMore && (
            <SkeletonChips count={2} offset={companies?.length ?? 0} />
          )}
          {hasMore && (
            <div ref={sentinelRef} aria-hidden className="h-px w-px shrink-0" />
          )}
        </ScrollContainer>
        {/* The fade alone did not say "this scrolls" — a stack of chips that
            happens to end softly reads as a stack of chips that ends. This
            button names the gesture and performs it, so the companies below
            are reachable without guessing that a wheel works over a block this
            small.

            Straddling the bottom edge rather than sitting under it: absolute,
            so it costs the column no height — the half under the composer is
            already tight — and half outside, so what it covers is the last
            12px of the fade, where a chip is too faint to be read or aimed at.
            Centered over the chips at `bottom-0` it landed squarely on the
            name of the last one. It only exists while something is hidden
            below. */}
        {canScrollDown && (
          <button
            type="button"
            onClick={scrollDownOnePage}
            aria-label={t("chat.new.recentCompanies.scrollForMore")}
            title={t("chat.new.recentCompanies.scrollForMore")}
            className="flex absolute -bottom-3 left-1/2 justify-center items-center rounded-full border shadow-sm transition-colors cursor-pointer -translate-x-1/2 size-6 bg-background/90 backdrop-blur-sm text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ChevronDownIcon className="size-3.5" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}
